import { ReactNode, useId, useState } from 'react';
import { UseFormRegisterReturn } from 'react-hook-form';
import InputMask from 'react-input-mask';

import InputOption, { optionsControl } from './InputOption';
import InputEmoji from './InputEmoji';
import maskToCurrency, { formatCurrencyString } from './masks/currency';
import { formatDateString } from './masks/date';

type Masks = typeof masks;

export type Props = {
  className?: string;
  name?: string;
  placeholder?: string;
  label?: string;
  caption?: string;
  disabled?: boolean;
  error?: boolean;
  size?: 'large' | 'small';
  prefixes?: ReactNode;
  mask?: keyof Masks;
  suffixes?: ReactNode;
  type?: string;
  form?: Partial<UseFormRegisterReturn>;
  emojiPicker?: boolean;
  options?: { label: string; value: string | boolean | number }[];
  optionFilter?: string;
  onOptionChange?: (value?: {
    label: string;
    value: string | boolean | number;
  }) => void;
  dataTestId?: string;
  onEmojiClick?: (value: string) => void;
};

export function inputContainerClasses({
  error,
  disabled,
  className,
}: Pick<Props, 'className' | 'error' | 'disabled'>) {
  const inputContainerClasses = ['form-input__container'];
  if (error) inputContainerClasses.push('form-input__container--error');
  if (disabled) inputContainerClasses.push('form-input__container--disabled');
  if (className) inputContainerClasses.push(className);

  return inputContainerClasses.join(' ');
}

const masks = {
  currency: 'R$ 99999999999999',
  date: '99/99/9999',
  hour: '99:99',
  zipCode: '99999-999',
  phone: '(99) 99999-9999',
  color: ['#', ...new Array(6).fill(/[a-f0-9]/i)],
  onlyNumbers: '9999999999',
};

const hiddenMaskPlaceholder = ['currency', 'color', 'zipCode', 'onlyNumbers'];

const inputMask = (mask: keyof Masks) => masks[mask];
const inputStateChange = (mask?: string) =>
  mask === 'currency'
    ? (newState: any, oldState: any, userInput: string, maskOptions: any) =>
        maskToCurrency(newState, oldState, userInput, maskOptions, 'Brazilian')
    : undefined;

// Masks rendered as plain inputs formatted on change: react-input-mask
// (uncontrolled) rewrites the DOM with its own internal state on every
// render, erasing values React Hook Form writes through the ref (setValue
// on edit forms) — those fields showed up empty when editing.
const selfFormattedMasks: Partial<
  Record<keyof Masks, (value: string) => string>
> = {
  currency: formatCurrencyString,
  date: formatDateString,
  onlyNumbers: (value) => value.replace(/\D/g, '').slice(0, 10),
};

export default function Input(props: Props) {
  const id = useId();
  const inputSize = props.size ? ` form-input--${props.size}` : '';
  const [activeOption, setActiveOption] = useState(-1);
  const [showOptions, setShowOptions] = useState(false);

  // react-input-mask v2 uses `inputRef` (not `ref`) to forward the DOM ref
  // to callers. react-hook-form passes `ref`, so we remap it here.
  const { ref: formRef, ...formWithoutRef } = props.form ?? {};

  const selfFormat = props.mask && selfFormattedMasks[props.mask];

  // Props shared by both the masked and the plain-input branches. Keeping them
  // in one object stops the two branches from drifting when attributes change.
  const commonProps = {
    type: props.type,
    id: `input-${id}`,
    name: props.name,
    'aria-invalid': props.error,
    className: `form-input${inputSize}`,
    placeholder: props.placeholder,
    disabled: props.disabled,
    'data-testid': props.dataTestId,
    ...formWithoutRef,
    ...optionsControl(
      showOptions,
      setShowOptions,
      activeOption,
      setActiveOption,
      props.options,
      props.optionFilter,
      props.onOptionChange
    ),
  };

  return (
    <div className={inputContainerClasses(props)}>
      {props.label && (
        <label htmlFor={`input-${id}`} className="form-input__label">
          {props.label}
        </label>
      )}
      <div className="form-input__addons__container">
        {props.prefixes}
        {selfFormat ? (
          <input
            ref={formRef}
            {...commonProps}
            onChange={(event) => {
              // eslint-disable-next-line no-param-reassign
              event.target.value = selfFormat(event.target.value);
              commonProps.onChange?.(event);
            }}
          />
        ) : props.mask ? (
          <InputMask
            mask={inputMask(props.mask)}
            maskChar={hiddenMaskPlaceholder.includes(props.mask) ? null : '_'}
            beforeMaskedValueChange={inputStateChange(props.mask)}
            inputRef={formRef}
            {...commonProps}
          />
        ) : (
          // No mask configured: render a plain input. Routing this case through
          // react-input-mask (with mask="") loses sync with values that React
          // Hook Form writes to the DOM directly (e.g. reset() after an async
          // defaultValues resolves), silently dropping all further keystrokes.
          <input ref={formRef} {...commonProps} />
        )}

        {props.suffixes && (
          <div className="form-input__addons__addon form-input__addons__addon--right">
            {props.suffixes}
          </div>
        )}
      </div>
      {showOptions && (
        <InputOption
          inputId={id}
          options={props.options}
          activeOption={activeOption}
          setActiveOption={setActiveOption}
          showOptions={showOptions}
          setShowOptions={setShowOptions}
          onOptionChange={props.onOptionChange}
          optionFilter={props.optionFilter}
        />
      )}
      {props.caption && (
        <span className="text-left form-input__caption">{props.caption}</span>
      )}
      {props.emojiPicker && <InputEmoji onEmojiClick={props.onEmojiClick} />}
    </div>
  );
}
